import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Card } from '../card/card';
import { Avatar } from '../avatar/avatar';
import { ZagIcon } from '../icon/icon';
import { ZagDatePipe } from '../formatting/zag-date.pipe';

/**
 * Data structure for PersonCard component
 */
export interface PersonCardData {
  id: string;
  firstName: string;
  lastName: string;
  /** Optional role from domain data (e.g., job role name) */
  role?: string;
  /** Optional profile picture URL or base64 data URI */
  profilePicture?: string | null;
  /** Optional date of birth (ISO date string) */
  dateOfBirth?: string | null;
  /** Optional primary email address */
  email?: string | null;
  /** Optional primary phone number */
  phone?: string | null;
  /** Optional team name */
  team?: string | null;
  /** Optional unique employee identifier */
  employeeNumber?: string | null;
}

/**
 * PersonCard Component
 *
 * Displays a person's basic information in a card format.
 * Uses the Card and Avatar components for consistent styling.
 *
 * @example
 * ```html
 * <zag-person-card
 *   [person]="personData"
 *   [editRoute]="['/persons', personData.id, 'edit']"
 *   (cardClick)="onPersonSelected($event)">
 * </zag-person-card>
 * ```
 */
@Component({
  selector: 'zag-person-card',
  templateUrl: './person-card.html',
  styleUrls: ['./person-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, Avatar, ZagIcon, ZagDatePipe],
  host: {
    '[class.zag-person-card]': 'true',
  },
})
export class PersonCard {
  private readonly router = inject(Router);

  /** The person data to display */
  readonly person = input.required<PersonCardData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Whether to show the delete button */
  readonly showDelete = input(false);

  /** Accessible label for the delete button */
  readonly deleteAriaLabel = input('');

  /** Emitted when the card is clicked */
  readonly cardClick = output<PersonCardData>();

  /** Emitted when the delete button is clicked */
  readonly deleteClick = output<PersonCardData>();

  /** Computed full name */
  readonly fullName = computed(() => {
    const p = this.person();
    return `${p.firstName} ${p.lastName}`;
  });

  /** Handle card click - navigates to editRoute if set, then emits cardClick */
  onCardClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.cardClick.emit(this.person());
  }

  /** Handle delete button click - stops propagation and emits deleteClick */
  onDeleteClick(event: MouseEvent): void {
    event.stopPropagation();
    this.deleteClick.emit(this.person());
  }
}
